// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Settings,
  User,
  
  Building,
  Globe,
  Zap,
  Palette,
  Activity,
  CheckCircle2,
  Flame,
  Cpu,
  Bot,
  HeartPulse,
  Crown,
  Gem,
  Sparkles,
  Check,
  Save,
  Sliders,
} from 'lucide-react';
import { ConnectedUserProfile } from './UserAccountMenu';
import { VisualThemeId } from '../types/theme';
import { VISUAL_THEME_PRESETS, getThemeConfig } from '../utils/themePresets';
import {
  SystemHealthConfig,
  SystemHealthMetrics,
  SystemHealthPreset,
} from '../types/systemHealth';
import {
  DEFAULT_HEALTH_CONFIG,
  STRICT_HEALTH_PRESET,
  RELAXED_HEALTH_PRESET,
} from '../utils/systemHealthEngine';
import './UserSettingsDrawer.css';

export interface UserSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: ConnectedUserProfile;
  onSaveProfile: (profile: ConnectedUserProfile) => void;
  activeThemeId: VisualThemeId;
  onSelectTheme: (themeId: VisualThemeId) => void;
  onOpenLocationManager?: () => void;
  onOpenTimezoneManager?: () => void;
  onOpenInteractionManager?: () => void;
  healthConfig?: SystemHealthConfig;
  onSaveHealthConfig?: (config: SystemHealthConfig) => void;
  healthMetrics?: SystemHealthMetrics;
  initialTab?: 'visual' | 'health' | 'profile' | 'system' | 'shortcuts';
}

// === COMPONENTE: DRAWER DE CONFIGURACIÓN DEL SISTEMA (5 PESTAÑAS) ===
export const UserSettingsDrawer: React.FC<UserSettingsDrawerProps> = ({
  isOpen,
  onClose,
  userProfile,
  onSaveProfile,
  activeThemeId,
  onSelectTheme,
  onOpenLocationManager,
  onOpenTimezoneManager,
  onOpenInteractionManager,
  healthConfig = DEFAULT_HEALTH_CONFIG,
  onSaveHealthConfig,
  healthMetrics,
  initialTab = 'visual',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'visual' | 'health' | 'profile' | 'system' | 'shortcuts'>(initialTab);
  const [themeFilterCategory, setThemeFilterCategory] = useState<'all' | 'avap_commander' | 'twopoint'>('all');

  // Form State Profile
  const [name, setName] = useState(userProfile.name);
  const [email, setEmail] = useState(userProfile.email || '');
  const [role, setRole] = useState(userProfile.role);
  const [status, setStatus] = useState<'online' | 'busy' | 'away'>(userProfile.status || 'online');

  // Health Configuration State
  const [localHealthConfig, setLocalHealthConfig] = useState<SystemHealthConfig>(healthConfig);

  // UI / App Preferences
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [showAgentLabels, setShowAgentLabels] = useState(true);
  const [highContrastGrid, setHighContrastGrid] = useState(false);
  const [incidentNotifications, setIncidentNotifications] = useState(true);

  const [isSavedAlert, setIsSavedAlert] = useState(false);
  const [themeChangeToast, setThemeChangeToast] = useState<string | null>(null);

  // === SINCRONIZA CONFIG DE SALUD Y PESTAÑA INICIAL CUANDO CAMBIAN LAS PROPS ===
  useEffect(() => {
    if (healthConfig) {
      setLocalHealthConfig(healthConfig);
    }
  }, [healthConfig, isOpen]);

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setName(userProfile.name);
      setEmail(userProfile.email || '');
      setRole(userProfile.role);
      setStatus(userProfile.status || 'online');
    }
  }, [isOpen, userProfile]);

  // === CERRAR CON CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }

      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveProfile({
      ...userProfile,
      name: name.trim() || userProfile.name,
      email: email.trim() || userProfile.email,
      role: role.trim() || userProfile.role,
      status,
    });
    setIsSavedAlert(true);
    setTimeout(() => {
      setIsSavedAlert(false);
      onClose();
    }, 600);
  };

  const handleApplyTheme = (themeId: VisualThemeId) => {
    onSelectTheme(themeId);
    const theme = getThemeConfig(themeId);
    setThemeChangeToast(`Visual theme "${theme.name}" applied system-wide`);
    setTimeout(() => {
      setThemeChangeToast(null);
    }, 2500);
  };

  const handleHealthConfigChange = (updated: Partial<SystemHealthConfig>) => {
    const newConf: SystemHealthConfig = {
      ...localHealthConfig,
      ...updated,
      weights: {
        ...localHealthConfig.weights,
        ...(updated.weights || {}),
      },
    };
    setLocalHealthConfig(newConf);
    if (onSaveHealthConfig) {
      onSaveHealthConfig(newConf);
    }
    setThemeChangeToast('Health parameters updated');
    setTimeout(() => {
      setThemeChangeToast(null);
    }, 2000);
  };

  const handleSelectHealthPreset = (preset: SystemHealthPreset) => {
    let targetConf: SystemHealthConfig;
    if (preset === 'strict') {
      targetConf = { ...STRICT_HEALTH_PRESET };
    } else if (preset === 'relaxed') {
      targetConf = { ...RELAXED_HEALTH_PRESET };
    } else {
      targetConf = { ...DEFAULT_HEALTH_CONFIG };
    }

    setLocalHealthConfig(targetConf);
    if (onSaveHealthConfig) {
      onSaveHealthConfig(targetConf);
    }
    setThemeChangeToast(`Health Criteria "${preset.toUpperCase()}" activated`);
    setTimeout(() => {
      setThemeChangeToast(null);
    }, 2000);
  };

  const currentActiveThemeConfig = getThemeConfig(activeThemeId);

  const filteredThemes = VISUAL_THEME_PRESETS.filter((t) => {
    if (themeFilterCategory === 'all') return true;
    return t.id === themeFilterCategory;
  });

  const getThemeIcon = (iconType: string) => {
    switch (iconType) {
      case 'flame':
        return <Flame className="twin-icon-sm twin-color-orange" />;
      case 'cpu':
        return <Cpu className="twin-icon-sm twin-color-violet" />;
      case 'bot':
        return <Bot className="twin-icon-sm twin-color-indigo" />;
      case 'gem':
        return <Gem className="twin-icon-sm twin-color-emerald" />;
      case 'crown':
        return <Crown className="twin-icon-sm twin-color-amber" />;
      case 'activity':
        return <Activity className="twin-icon-sm twin-color-theme-accent" />;
      case 'zap':
        return <Zap className="twin-icon-sm twin-color-yellow" />;
      case 'sparkles':
        return <Sparkles className="twin-icon-sm twin-color-lime" />;
      default:
        return <Sparkles className="twin-icon-sm twin-color-theme-accent" />;
    }
  };

  // === RENDER ===
  return (
    <div ref={drawerRef} id="user-settings-drawer-container" className="twin-settings-drawer">
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-drawer-icon-badge twin-settings-header-icon">
            <Settings className="twin-icon-sm" />
          </div>
          <div className="twin-drawer-header-text">
            <h2 className="twin-drawer-title twin-settings-title-heavy">System Settings</h2>
            <p className="twin-drawer-subtitle twin-settints-subtitle">Visual theme, health, profile, and preferences</p>
          </div>
        </div>

        <button type="button" onClick={onClose} className="twin-drawer-close-btn" aria-label="Close settings drawer" title="Close drawer (Esc)">
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === BARRA DE PESTAÑAS === */}
      <div className="twin-settings-tabs">
        {/* TAB 1: VISUAL THEME */}
        <button type="button" onClick={() => setActiveTab('visual')} className={`twin-settings-tab ${activeTab === 'visual' ? 'twin-settings-tab--active' : ''}`}>
          <Palette className="twin-icon-xs " />
          <span>Visual Theme</span>
          <span className="twin-settings-tab-theme-badge">{currentActiveThemeConfig.name.split(' ')[0]}</span>
        </button>

        {/* TAB 2: SYSTEM HEALTH */}
        <button type="button" onClick={() => setActiveTab('health')} className={`twin-settings-tab ${activeTab === 'health' ? 'twin-settings-tab--active' : ''}`}>
          <HeartPulse className="twin-icon-xs " />
          <span>Health</span>
          {healthMetrics ? (
            <span
              className="twin-settings-tab-health-badge"
              style={{
                backgroundColor: `${healthMetrics.statusColor}20`,
                borderColor: `${healthMetrics.statusColor}60`,
                color: healthMetrics.statusColor,
              }}
            >
              {healthMetrics.overallScore}%
            </span>
          ) : (
            <span className="twin-settings-tab-health-badge twin-settings-tab-health-badge--default">{localHealthConfig.preset.toUpperCase()}</span>
          )}
        </button>

        {/* TAB 3: OPERATOR PROFILE */}
        <button type="button" onClick={() => setActiveTab('profile')} className={`twin-settings-tab ${activeTab === 'profile' ? 'twin-settings-tab--active' : ''}`}>
          <User className="twin-icon-xs" />
          <span>Profile</span>
        </button>

        {/* TAB 4: SYSTEM PREFERENCES */}
        <button type="button" onClick={() => setActiveTab('system')} className={`twin-settings-tab ${activeTab === 'system' ? 'twin-settings-tab--active' : ''}`}>
          <Sliders className="twin-icon-xs" />
          <span>Preferences</span>
        </button>

        {/* TAB 5: MANAGERS */}
        <button type="button" onClick={() => setActiveTab('shortcuts')} className={`twin-settings-tab ${activeTab === 'shortcuts' ? 'twin-settings-tab--active' : ''}`}>
          <Zap className="twin-icon-xs" />
          <span>Managers</span>
        </button>
      </div>

      {/* === CUERPO CON SCROLL === */}
      <div className="twin-settings-body">
        {/* === TAB 1: VISUAL THEME === */}
        {activeTab === 'visual' && (
          <div className="twin-settings-tab-panel">
            <div className="twin-settings-intro-card">
              <div>
                <h3 className="twin-settings-intro-title"><Palette className="twin-icon-xs twin-color-theme-accent" />
                <span>Simulation Visual Styles &amp; Themes</span>
                </h3>
                <p className="twin-settings-intro-desc">
                  Select a visual preset to transform color palettes, menu formatting, borders, and canvas styling.
                </p>
              </div>
              <div className="twin-settings-active-badge">
                <span className="twin-settings-active-dot" />
                Active: <strong className="twin-settings-active-badge-name">{currentActiveThemeConfig.name}</strong>
              </div>
            </div>

            <div className="twin-settings-filter-row">
              <button type="button" onClick={() => setThemeFilterCategory('all')} className={`twin-settings-filter-pill ${themeFilterCategory === 'all' ? 'twin-settings-filter-pill--all-active' : ''}`}>
                All Themes ({VISUAL_THEME_PRESETS.length})
              </button>
              <button type="button" onClick={() => setThemeFilterCategory('twopoint')} className={`twin-settings-filter-pill ${themeFilterCategory === 'twopoint' ? 'twin-settings-filter-pill--twopoint-active' : ''}`}>
                <Activity className="twin-icon-xs twin-color-teal" />
                AVAP Digital Twin (Default)
              </button>
              <button type="button" onClick={() => setThemeFilterCategory('avap_commander')} className={`twin-settings-filter-pill ${themeFilterCategory === 'avap_commander' ? 'twin-settings-filter-pill--commander-active' : ''}`}>
                <Cpu className="twin-icon-xs twin-color-indigo" />
                Commander Collection
              </button>
            </div>

            <div className="twin-settings-theme-grid">
              {filteredThemes.map((preset) => {
                const isSelected = preset.id === activeThemeId;
                return (
                  <div key={preset.id} className={`twin-settings-theme-card ${isSelected ? 'twin-settings-theme-card--selected' : ''}`}>
                    <div className="twin-settings-theme-card-top">
                      <div className="twin-settings-theme-card-info">
                        <div
                          className="twin-settings-theme-card-icon-wrap"
                          style={{
                            backgroundColor: preset.styles.cardBgSubtle || 'var(--theme-card-bg-subtle)',
                            borderColor: preset.styles.primaryColor || 'var(--theme-primary-hover)',
                          }}
                        >
                          {getThemeIcon(preset.iconType)}
                        </div>
                        <div className="twin-settings-theme-card-text">
                          <div className="twin-settings-theme-card-name-row">
                            <span className="twin-settings-theme-card-name">{preset.name}</span>
                            {preset.id === 'twopoint' && <span className="twin-settings-theme-card-default-badge">DEFAULT</span>}
                          </div>
                          <span className="twin-settings-theme-card-tagline">{preset.tagline || preset.gameInspiration}</span>
                        </div>
                      </div>

                      {isSelected ? (
                        <div className="twin-settings-theme-active-pill">
                          <Check className="twin-icon-xs" />
                          <span>Active</span>
                        </div>
                      ) : (
                        <button type="button" onClick={() => handleApplyTheme(preset.id)} className="twin-settings-theme-apply-btn">
                          Apply
                        </button>
                      )}
                    </div>

                    <p className="twin-settings-theme-card-desc">{preset.description}</p>

                    <div className="twin-settings-theme-card-footer">
                      <div className="twin-settings-theme-palette-row">
                        <span className="twin-settings-theme-palette-label">Palette:</span>
                        <div className="twin-settings-theme-swatches">
                          {preset.previewColors.map((color, cIdx) => (
                            <span key={cIdx} className="twin-settings-theme-swatch" style={{ backgroundColor: color }} title={`Color ${cIdx + 1}: ${color}`} />
                          ))}
                        </div>
                      </div>
                      <span className="twin-settings-theme-border-label">Border: {preset.styles.uiBorderRadius || '12px'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === TAB 2: SYSTEM HEALTH === */}
        {activeTab === 'health' && (
          <div className="twin-settings-tab-panel">
            {healthMetrics && (
              <div
                className="twin-settings-health-status-card"
                style={{
                  backgroundColor: healthMetrics.statusBadgeBg,
                  borderColor: healthMetrics.statusBorderColor,
                }}
              >
                <div className="twin-settings-health-status-top">
                  <div className="twin-settings-health-status-left">
                    <HeartPulse className="twin-icon-md twin-settings-health-pulse-icon" />
                    <div>
                      <h4 className="twin-settings-health-status-title">
                        Overall Status: {healthMetrics.statusLabel} ({healthMetrics.overallScore}%)
                      </h4>
                      <p className="twin-settings-health-status-sub">
                        {healthMetrics.placedAgentsCount} agents • {healthMetrics.totalFloorAreaM2.toFixed(0)} m² floor area • {healthMetrics.measuredFps} FPS
                      </p>
                    </div>
                  </div>
                  <span className="twin-settings-health-preset-tag">{localHealthConfig.preset.toUpperCase()}</span>
                </div>

                <div className="twin-settings-health-kpi-grid">
                  <div className="twin-settings-health-kpi-box">
                    <span className="twin-settings-health-kpi-label">Structural</span>
                    <span className="twin-settings-health-kpi-value twin-settings-health-kpi-value--cyan">{healthMetrics.structuralScore}%</span>
                  </div>
                  <div className="twin-settings-health-kpi-box">
                    <span className="twin-settings-health-kpi-label">Spatial</span>
                    <span className="twin-settings-health-kpi-value twin-settings-health-kpi-value--emerald">{healthMetrics.spatialScore}%</span>
                  </div>
                  <div className="twin-settings-health-kpi-box">
                    <span className="twin-settings-health-kpi-label">Performance</span>
                    <span className="twin-settings-health-kpi-value twin-settings-health-kpi-value--amber">{healthMetrics.performanceScore}%</span>
                  </div>
                  <div className="twin-settings-health-kpi-box">
                    <span className="twin-settings-health-kpi-label">Activity</span>
                    <span className="twin-settings-health-kpi-value twin-settings-health-kpi-value--purple">{healthMetrics.activityScore}%</span>
                  </div>
                </div>
              </div>
            )}

            <div className="twin-settings-card">
              <h4 className="twin-settings-card-subtitle twin-settings-card-subtitle--caps">
                <CheckCircle2 className="twin-icon-xs twin-color-theme-accent" />
                <span>Health Evaluation Criteria</span>
              </h4>
              <div className="twin-settings-health-preset-grid">
                <button
                  type="button"
                  onClick={() => handleSelectHealthPreset('balanced')}
                  className={`twin-settings-health-preset-btn ${localHealthConfig.preset === 'balanced' ? 'twin-settings-health-preset-btn--balanced-active' : ''}`}
                >
                  <span className="twin-settings-health-preset-btn-title">Balanced</span>
                  <span className="twin-settings-health-preset-btn-sub">Default (4.0m²)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectHealthPreset('strict')}
                  className={`twin-settings-health-preset-btn ${localHealthConfig.preset === 'strict' ? 'twin-settings-health-preset-btn--strict-active' : ''}`}
                >
                  <span className="twin-settings-health-preset-btn-title">Strict</span>
                  <span className="twin-settings-health-preset-btn-sub">Audit (6.0m²)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectHealthPreset('relaxed')}
                  className={`twin-settings-health-preset-btn ${localHealthConfig.preset === 'relaxed' ? 'twin-settings-health-preset-btn--relaxed-active' : ''}`}
                >
                  <span className="twin-settings-health-preset-btn-title">Relaxed</span>
                  <span className="twin-settings-health-preset-btn-sub">Flexible (2.5m²)</span>
                </button>
              </div>
            </div>

            <div className="twin-settings-card">
              <h4 className="twin-settings-card-subtitle twin-settings-card-subtitle--caps">
                <Sliders className="twin-icon-xs twin-color-theme-accent" />
                <span>Threshold Calibration</span>
              </h4>

              {/* Slider 1: Área mínima por agente */}
              <div className="twin-settings-slider-row">
                <div className="twin-settings-slider-label-row">
                  <span className="twin-settings-slider-label">Min Area per Agent (m²)</span>
                  <span className="twin-settings-slider-value twin-settings-slider-value--cyan">{localHealthConfig.minAreaPerAgentM2} m²</span>
                </div>
                <input
                  type="range"
                  min="1.5"
                  max="10.0"
                  step="0.5"
                  value={localHealthConfig.minAreaPerAgentM2}
                  onChange={(e) => handleHealthConfigChange({ minAreaPerAgentM2: parseFloat(e.target.value) })}
                  className="twin-settings-range twin-settings-range--cyan"
                />
              </div>

              {/* Slider 2: Max % Agentes Inactivos */}
              <div className="twin-settings-slider-row">
                <div className="twin-settings-slider-label-row">
                  <span className="twin-settings-slider-label">Idle Tolerance (% Rest)</span>
                  <span className="twin-settings-slider-value twin-settings-slider-value--amber">{localHealthConfig.maxAgentIdlePercent}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="80"
                  step="5"
                  value={localHealthConfig.maxAgentIdlePercent}
                  onChange={(e) => handleHealthConfigChange({ maxAgentIdlePercent: parseInt(e.target.value) })}
                  className="twin-settings-range twin-settings-range--amber"
                />
              </div>

              {/* Slider 3: Target FPS */}
              <div className="twin-settings-slider-row">
                <div className="twin-settings-slider-label-row">
                  <span className="twin-settings-slider-label">Target Ticks / Performance (FPS)</span>
                  <span className="twin-settings-slider-value twin-settings-slider-value--purple">{localHealthConfig.targetFps} FPS</span>
                </div>
                <input
                  type="range"
                  min="30"
                  max="60"
                  step="5"
                  value={localHealthConfig.targetFps}
                  onChange={(e) => handleHealthConfigChange({ targetFps: parseInt(e.target.value) })}
                  className="twin-settings-range twin-settings-range--purple"
                />
              </div>

              <div className="twin-settings-toggles-group">
                <label className="twin-settings-toggle-row">
                  <span>Allow zone overlap without penalty</span>
                  <input
                    type="checkbox"
                    checked={localHealthConfig.allowRoomOverlap}
                    onChange={(e) => handleHealthConfigChange({ allowRoomOverlap: e.target.checked })}
                    className="twin-settings-checkbox"
                  />
                </label>
                <label className="twin-settings-toggle-row">
                  <span>Continuous live pulse animation on HUD</span>
                  <input
                    type="checkbox"
                    checked={localHealthConfig.showLivePulseAnimation}
                    onChange={(e) => handleHealthConfigChange({ showLivePulseAnimation: e.target.checked })}
                    className="twin-settings-checkbox"
                  />
                </label>
                <label className="twin-settings-toggle-row">
                  <span>Highlight degraded capacity zones on map</span>
                  <input
                    type="checkbox"
                    checked={localHealthConfig.highlightDegradedZones}
                    onChange={(e) => handleHealthConfigChange({ highlightDegradedZones: e.target.checked })}
                    className="twin-settings-checkbox"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* === TAB 3: OPERATOR PROFILE === */}
        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="twin-settings-tab-panel">
            <div className="twin-settings-card">
              <div className="twin-settings-profile-header-row">
                <div className="twin-settings-profile-avatar">
                  {name
                    .split(' ')
                    .map((p) => p[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join('')
                    .toUpperCase() || 'OP'}
                </div>
                <div>
                  <h3 className="twin-settings-profile-name">{name || 'Operator'}</h3>
                  <p className="twin-settings-profile-role">{role}</p>
                </div>
              </div>

              <div className="twin-settings-field">
                <label className="twin-settings-field-label">Full Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Raul Nogales" className="twin-settings-input" />
              </div>

              <div className="twin-settings-field">
                <label className="twin-settings-field-label">Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@domain.com" className="twin-settings-input" />
              </div>

              <div className="twin-settings-field">
                <label className="twin-settings-field-label">Role / Title in Digital Twin</label>
                <input type="text" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. System Administrator" className="twin-settings-input" />
              </div>

              <div className="twin-settings-field">
                <label className="twin-settings-field-label">Availability Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="twin-settings-select">
                  <option value="online">🟢 Online / Active</option>
                  <option value="busy">🔴 Busy / In Simulation</option>
                  <option value="away">🟡 Away / Standby</option>
                </select>
              </div>
            </div>
          </form>
        )}

        {/* === TAB 4: SYSTEM PREFERENCES === */}
        {activeTab === 'system' && (
          <div className="twin-settings-tab-panel twin-settings-tab-panel--tight">
            <div className="twin-settings-card">
              <h4 className="twin-settings-card-subtitle twin-settings-card-subtitle--caps">
              <Sliders className="twin-icon-xs twin-color-theme-accent" />
              <span>Interface &amp; Simulation Preferences</span>
            </h4>

              <label className="twin-settings-pref-row">
                <div className="twin-settings-pref-info">
                  <span className="twin-settings-pref-title">Automatic Auto-save</span>
                  <span className="twin-settings-pref-desc">Persist real-time changes to local storage</span>
                </div>
                <input type="checkbox" checked={autoSaveEnabled} onChange={(e) => setAutoSaveEnabled(e.target.checked)} className="twin-settings-checkbox" />
              </label>

              <label className="twin-settings-pref-row">
                <div className="twin-settings-pref-info">
                  <span className="twin-settings-pref-title">Floating agent labels</span>
                  <span className="twin-settings-pref-desc">Display name, role, and status over each avatar on the canvas</span>
                </div>
                <input type="checkbox" checked={showAgentLabels} onChange={(e) => setShowAgentLabels(e.target.checked)} className="twin-settings-checkbox" />
              </label>

              <label className="twin-settings-pref-row">
                <div className="twin-settings-pref-info">
                  <span className="twin-settings-pref-title">High-contrast grid</span>
                  <span className="twin-settings-pref-desc">Enhance visibility of isometric floor grid lines</span>
                </div>
                <input type="checkbox" checked={highContrastGrid} onChange={(e) => setHighContrastGrid(e.target.checked)} className="twin-settings-checkbox" />
              </label>

              <label className="twin-settings-pref-row">
                <div className="twin-settings-pref-info">
                  <span className="twin-settings-pref-title">Incident notifications</span>
                  <span className="twin-settings-pref-desc">Broadcast visual alerts upon detecting spatial capacity overloads</span>
                </div>
                <input type="checkbox" checked={incidentNotifications} onChange={(e) => setIncidentNotifications(e.target.checked)} className="twin-settings-checkbox" />
              </label>
            </div>
          </div>
        )}

        {/* === TAB 5: MANAGERS & SHORTCUTS === */}
        {activeTab === 'shortcuts' && (
          <div className="twin-settings-tab-panel twin-settings-tab-panel--tight">
            <div className="twin-settings-managers-intro-card">
              <h4 className="twin-settings-card-subtitle twin-settings-card-subtitle--caps">
                <Zap className="twin-icon-xs twin-color-theme-accent" />
                <span>Management Modules</span>
              </h4>
              <p className="twin-settings-managers-intro-desc">Direct shortcuts to digital twin control centers:</p>
            </div>

            {onOpenLocationManager && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenLocationManager();
                }}
                className="twin-settings-manager-btn"
              >
                <div className="twin-settings-manager-left">
                  <div className="twin-settings-manager-icon-wrap twin-settings-manager-icon-wrap--cyan">
                    <Building className="twin-icon-sm" />
                  </div>
                  <div>
                    <div className="twin-settings-manager-title twin-settings-manager-title--cyan">Floors &amp; Locations Manager</div>
                    <div className="twin-settings-manager-desc">Create floors, transfer agents, and edit layouts</div>
                  </div>
                </div>
                <span className="twin-settings-manager-arrow twin-settings-manager-arrow--cyan">Open →</span>
              </button>
            )}

            {onOpenTimezoneManager && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenTimezoneManager();
                }}
                className="twin-settings-manager-btn"
              >
                <div className="twin-settings-manager-left">
                  <div className="twin-settings-manager-icon-wrap twin-settings-manager-icon-wrap--emerald">
                    <Globe className="twin-icon-sm" />
                  </div>
                  <div>
                    <div className="twin-settings-manager-title twin-settings-manager-title--emerald">Timezone &amp; World Clocks Manager</div>
                    <div className="twin-settings-manager-desc">Configure IANA zones and world office clocks</div>
                  </div>
                </div>
                <span className="twin-settings-manager-arrow twin-settings-manager-arrow--emerald">Open →</span>
              </button>
            )}

            {onOpenInteractionManager && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenInteractionManager();
                }}
                className="twin-settings-manager-btn"
              >
                <div className="twin-settings-manager-left">
                  <div className="twin-settings-manager-icon-wrap twin-settings-manager-icon-wrap--amber">
                    <Zap className="twin-icon-sm" />
                  </div>
                  <div>
                    <div className="twin-settings-manager-title twin-settings-manager-title--amber">Interactions &amp; Dialogues Manager</div>
                    <div className="twin-settings-manager-desc">Customize routines, agent dialogue scripts, and tasks</div>
                  </div>
                </div>
                <span className="twin-settings-manager-arrow twin-settings-manager-arrow--amber">Open →</span>
              </button>
            )}
          </div>
        )}
      </div>

            {/* === FOOTER === */}
      <div className="twin-drawer-footer">
        <div className="twin-settings-footer-left">
          {themeChangeToast && (
            <span className="twin-settings-toast">
              <CheckCircle2 className="twin-icon-xs" />
              {themeChangeToast}
            </span>
          )}
          {isSavedAlert && (
            <span className="twin-settings-toast twin-settings-toast--success">
              <Check className="twin-icon-xs" />
              Profile saved!
            </span>
          )}
        </div>
        <div className="twin-settings-footer-actions">
          <button type="button" onClick={onClose} className="twin-btn-cancel">
            Close
          </button>
          {activeTab === 'profile' && (
            <button type="button" onClick={handleSubmit} className="twin-btn-save">
              <Save className="twin-icon-xs" />
              <span>Save</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};